<template>
  <div
    :class="[
      'hilo-select',
      theme === 'grey' ? 'grey-theme' : '',
      disabled ? 'disabled' : '',
      size === 'sm' ? 'hilo-select-sm' : '',
    ]"
    :style="{
      ...customStyle,
      borderRadius: inputBorderRadiusStyle,
    }"
    @keydown="!disabled && handleKeydown"
  >
    <input
      v-if="searchable"
      type="text"
      v-model="searchQuery"
      @focus="openDropdown"
      @input="filterOptions"
      :class="['hilo-select-input', theme === 'grey' ? 'grey-theme-input' : '']"
      :placeholder="searchPlaceholder"
      :id="id ? id : 'select' + JSON.stringify(options[0])"
      :disabled="disabled"
      :style="{
        backgroundColor: customStyle?.backgroundColor,
        borderRadius: inputBorderRadiusStyle,
      }"
    />
    <span v-else-if="multiSelect && selectedOptions.length > 0">
      {{ selectedOptions.map((option) => option.name).join(", ") }}
    </span>
    <span v-else>{{
      selectedOptions[0]?.name || searchQuery || placeholder
    }}</span>
    <span class="icon-container" v-if="!disabled" @click="toggleDropdown">
      <i
        v-if="selectedOptions.length === 0"
        class="fas fa-chevron-down hilo-select-arrow"
        :class="[
          { rotate: dropdownOpen },
          theme === 'grey' ? 'grey-theme-arrow' : '',
        ]"
      ></i>
      <i
        v-else
        class="fas fa-times hilo-select-arrow"
        @click.stop="clearDropdown"
      ></i>
    </span>
    <div
      v-if="dropdownOpen"
      :class="[
        'hilo-select-dropdown',
        'scroll',
        theme === 'grey' ? 'grey-theme-dropdown' : '',
      ]"
      @mouseleave="handleMouseLeave"
      @mouseenter="handleMouseEnter"
    >
      <div
        v-if="showSelectAll && multiSelect && !checkAllSelected"
        @click="selectAllOptions"
        class="hilo-select-option"
      >
        Select All
      </div>
      <div
        v-for="(option, index) in filteredOptions"
        :key="option.id"
        :class="[
          'hilo-select-option',
          {
            selected: isSelected(option),
            highlighted: index === highlightedIndex,
            disabled: disabled,
          },
        ]"
        @click="!disabled && selectOption(option)"
        @mouseover="!disabled && handleMouseOver(index)"
      >
        {{ option.name }}
        <i v-if="isSelected(option)" class="fas fa-check float-right"></i>
      </div>
      <div
        :class="'hilo-select-option-disabled'"
        v-if="filteredOptions.length === 0"
      >
        None
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "HiloSelect",
  props: {
    options: {
      type: Array,
      required: true,
    },
    value: {
      type: [String, Number, Array, Boolean],
      default: null,
    },
    placeholder: {
      type: String,
      default: "Select...",
    },
    placeholderColor: {
      type: String
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    multiSelect: {
      type: Boolean,
      default: false,
    },
    showSelectAll: {
      type: Boolean,
      default: false,
    },
    hoverClose: {
      type: Boolean,
      default: false,
    },
    theme: {
      type: String,
      default: "default",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    customStyle: {
      type: Object,
      default: () => ({}),
    },
    size: {
      type: [String],
      default: "",
    },
    id: {
      type: String,
      default: "",
    },
    enableManualInput: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: "",
      selectedOptions: [],
      dropdownOpen: false,
      searchPlaceholder: this.placeholder,
      highlightedIndex: -1,
      hoverTimeout: null,
      inputBorderRadiusStyle: "10px",
    };
  },
  computed: {
    filteredOptions() {
      if (!this.searchable || !this.searchQuery) {
        return this.options;
      }
      return this.options.filter((option) =>
        option.name.toLowerCase().includes(this.searchQuery.toLowerCase()),
      );
    },
    checkAllSelected() {
      return this.selectedOptions.length === this.options.length;
    },
  },
  watch: {
    value(newVal) {
      this.setSelectedOptions(newVal);
    },
    searchQuery(newVal) {
      if (this.enableManualInput && !this.selectedOptions.length) {
        this.$emit("input", newVal);
      }
    },
  },
  methods: {
    setSelectedOptions(value) {
      if (Array.isArray(value)) {
        this.selectedOptions = this.options.filter((option) =>
          value.includes(option.id),
        );
      } else {
        this.selectedOptions = this.options.filter(
          (option) => option.id === value,
        );
      }
      this.updatePlaceholder();
    },
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
      if (this.dropdownOpen) {
        this.openDropdown();
        this.highlightedIndex = -1;
      } else {
        this.closeDropdown();
      }
    },
    openDropdown() {
      this.dropdownOpen = true;
      this.highlightedIndex = -1;
      this.$nextTick(() => {
        this.adjustDropdownPosition();
        const scrollDivs = this.$el.querySelectorAll(".scroll");
        scrollDivs.forEach((div) => {
          div.addEventListener("wheel", this.preventScroll, { passive: false });
        });
      });
    },
    adjustDropdownPosition() {
      const dropdown = this.$el.querySelector(".hilo-select-dropdown");
      if (!dropdown) return;

      const rect = this.$el.getBoundingClientRect();
      const dropdownHeight = dropdown.offsetHeight;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        dropdown.style.top = "auto";
        dropdown.style.bottom = "100%";
        dropdown.style.borderRadius = "10px 10px 0 0";
        this.inputBorderRadiusStyle = "0 0 10px 10px";
      } else {
        dropdown.style.top = "100%";
        dropdown.style.bottom = "auto";
        dropdown.style.borderRadius = "0 0 10px 10px";
        this.inputBorderRadiusStyle = "10px 10px 0 0";
      }
    },
    closeDropdown() {
      const scrollDivs = this.$el.querySelectorAll(".scroll");
      scrollDivs.forEach((div) => {
        div.removeEventListener("wheel", this.preventScroll);
      });
      this.dropdownOpen = false;
      this.highlightedIndex = -1;
      this.inputBorderRadiusStyle = "10px";
    },
    preventScroll(event) {
      const target = event.target;
      event.stopPropagation();
      target.scrollTop += event.deltaY;
    },
    filterOptions() {
      this.dropdownOpen = true;
    },
    handleOutsideClick(event) {
      const dropdown = this.$el;
      if (!dropdown.contains(event.target)) {
        this.closeDropdown();
      }
    },
    handleKeydown(event) {
      switch (event.key) {
        case "Enter":
          if (this.dropdownOpen) {
            if (this.highlightedIndex >= 0) {
              this.selectOption(this.filteredOptions[this.highlightedIndex]);
            }
            this.closeDropdown();
          } else {
            this.openDropdown();
          }
          event.preventDefault();
          break;

        case "ArrowDown":
          if (!this.dropdownOpen) {
            this.openDropdown();
          } else {
            this.highlightedIndex =
              (this.highlightedIndex + 1) % this.filteredOptions.length;
          }
          event.preventDefault();
          break;

        case "ArrowUp":
          if (this.dropdownOpen) {
            this.highlightedIndex =
              (this.highlightedIndex - 1 + this.filteredOptions.length) %
              this.filteredOptions.length;
          }
          event.preventDefault();
          break;

        case "Tab":
          this.closeDropdown();
          break;
      }
    },
    handleMouseOver(index) {
      this.highlightedIndex = index;
    },
    selectOption(option) {
      if (this.multiSelect) {
        if (this.isSelected(option)) {
          this.selectedOptions = this.selectedOptions.filter(
            (selected) => selected.id !== option.id,
          );
        } else {
          this.selectedOptions.push(option);
        }
      } else {
        this.selectedOptions = [option];
        this.searchQuery = "";
        this.closeDropdown();
      }
      this.emitSelection();
      this.$forceUpdate()
      this.updatePlaceholder()
    },
    selectAllOptions() {
      this.selectedOptions = [...this.options];
      this.emitSelection();
    },
    updatePlaceholder() {
      this.searchPlaceholder = this.placeholder;
      let extraCount = 0;

      for (let option of this.selectedOptions) {
        if (this.selectedOptions.indexOf(option) >= 2) {
          extraCount++;
          continue;
        }
        if (this.searchPlaceholder === this.placeholder) {
          this.searchPlaceholder = option.name;
        } else {
          this.searchPlaceholder += ", " + option.name;
        }
      }

      if (extraCount > 0) {
        this.searchPlaceholder += ", +" + extraCount;
      }

      const placeholder = document.getElementById(
        this.id ? this.id : "select" + JSON.stringify(this.options[0])
      );

      if (placeholder) {
        let placeholderColor = "#9b9b9b";

        if (this.theme === "grey") {
          placeholderColor = "black";
        } else if (this.theme === "custom" && this.placeholderColor) {
          placeholderColor = this.placeholderColor;
        }

        this.$nextTick(() => {
          placeholder.style.setProperty("--placeholder-color", placeholderColor);

          const style = document.createElement("style");
          style.innerHTML = `.grey-theme-input::placeholder {color: var(--placeholder-color) !important;}`;
          document.head.appendChild(style);
        });
      }
    },
    emitSelection() {
      let emittedValue;
      if (this.selectedOptions.length) {
        emittedValue = this.multiSelect
          ? this.selectedOptions.map((option) => option.id)
          : this.selectedOptions[0]?.id;
      } else if (this.enableManualInput) {
        emittedValue = this.searchQuery;
      } else {
        emittedValue = this.multiSelect ? [] : null;
      }

      this.$emit("input", emittedValue);
      this.$emit("change", emittedValue);
    },
    isSelected(option) {
      return (
        Array.isArray(this.selectedOptions) &&
        this.selectedOptions.some((selected) => selected.id === option.id)
      );
    },
    clearDropdown() {
      this.selectedOptions = [];
      this.searchQuery = "";
      this.emitSelection();
      this.closeDropdown();
      this.updatePlaceholder()
    },
    handleMouseEnter() {
      clearTimeout(this.hoverTimeout);
    },
    handleMouseLeave() {
      if (this.hoverClose) {
        this.hoverTimeout = setTimeout(() => {
          this.closeDropdown();
        }, 500);
      }
    },
  },
  mounted() {
    this.setSelectedOptions(this.value);
    document.addEventListener("mousedown", this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
    clearTimeout(this.hoverTimeout);
  },
};
</script>

<style scoped lang="scss">
.hilo-select-sm {
  input {
    &::placeholder {
      font-family: Helvetica, Arial, sans-serif;
      font-size: 15px;
      font-style: normal;
      font-weight: 400;
    }
  }

  .icon-container {
    .hilo-select-arrow {
      font-size: 15px;
    }
  }
}

.hilo-select {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #ccc;
  border-radius: 10px;
  color: black;
  padding: 3px;
  cursor: pointer;
  background-color: white;
  font-size: 15px;
}

/*noinspection CssUnusedSymbol */
.hilo-select.disabled {
  pointer-events: none;
  background-color: #f5f5f5;
  color: #f5f5f5;
  cursor: not-allowed;
}

.hilo-select-input {
  padding: 0;
  margin: 0 0 0 5px;
  border: none;
  cursor: pointer;
  width: 100%;
  background-color: inherit;
}

.hilo-select-input:disabled {
  background-color: #f5f5f5;
  color: #a0a0a0;
  cursor: not-allowed;
}

.hilo-select-input::placeholder {
  padding-left: 5px;
  color: black;
}

/*noinspection CssUnusedSymbol */
.grey-theme-input {
  background-color: #f5f5f5;
  color: black;
}

/*noinspection CssUnusedSymbol */
.grey-theme-input::placeholder {
  color: #9b9b9b;
}

/*noinspection CssUnusedSymbol */
.grey-theme-arrow {
  color: #9b9b9b;
}

.hilo-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: calc(100%);
  padding-left: 13px;
  border: 1px solid #ccc;
  border-radius: 10px;
  background-color: white;
  max-height: 250px;
  overflow-y: auto;
  z-index: 50;
}

.hilo-select-option {
  padding: 8px;
  cursor: pointer;
}

/*noinspection CssUnusedSymbol */
.hilo-select-option.disabled {
  pointer-events: none;
  color: #a0a0a0;
  cursor: not-allowed;
}

/*noinspection CssUnusedSymbol */
.hilo-select-option:hover,
.hilo-select-option.highlighted {
  background-color: lightgrey;
}

/*noinspection CssUnusedSymbol */
.hilo-select-option.selected {
  font-weight: bold;
}

.hilo-select-arrow {
  margin-left: 8px;
  transition: transform 0.2s ease;
  color: black;
}

.icon-container {
  padding-right: 12px;
}

.hilo-select-option-disabled {
  padding: 8px;
  cursor: not-allowed;
  color: grey;
}

/*noinspection CssUnusedSymbol */
.hilo-select-arrow.rotate {
  transform: rotate(180deg);
}

/*noinspection CssUnusedSymbol */
.grey-theme {
  background-color: #f5f5f5;
  border: none;
  color: black;
  height: 50px;
}

.grey-theme .hilo-select-arrow {
  color: rgba(64, 64, 64, 0.5);
}

/*noinspection CssUnusedSymbol */
.grey-theme-dropdown {
  border: 1px solid #f0f0f0;
  background-color: #f0f0f0;
}
</style>
